/**
 * @file ProductInventorySection.tsx
 * @description «Фасовки» карточки товара — компактный список в боковой
 * колонке (phase08.5; phase19.13 — архивные фасовки; phase19.22 — печать
 * этикеток; phase19.31 — список строк вместо плиток с полями, разбор B).
 *
 * Строка — `ProductSkuRow`: название, цена, артикул · вес · остаток, «⋯».
 * Цена и остаток правятся в окне фасовки (`SkuEditDialog`). Фасовку со
 * складской историей сервер не удаляет, а отправляет в архив — текст итога
 * даёт сервер (`useProductSkus`). Печать — право склада (`SHOP_WMS_VIEW` или
 * `SHOP_WMS_MANAGE`, как на сервере): без него пункта нет.
 */

import { Package, Plus } from "lucide-react";
import { useState } from "react";

import { useHasPermission } from "../../../../auth/hooks";
import { Button } from "../../../../components/ui/button";
import { NoticeStrip } from "../../../../components/ui/NoticeStrip";
import { ConfirmDialog } from "../../../../components/ui/ConfirmDialog";
import type { CatalogCategory, CatalogSku } from "../../../../lib/api-catalog";
import type { LabelTemplate } from "../../../../lib/api-product-kinds";
import { generateSkuCode } from "../sku-code";
import { ProductSkuRow } from "./ProductSkuRow";
import { SectionCard } from "./SectionCard";
import { SkuEditDialog } from "./SkuEditDialog";
import { SkuLabelPrintDialog } from "./SkuLabelPrintDialog";
import { useProductSkus } from "./useProductSkus";

interface Props {
  productId: string;
  productSlug?: string;
  /** Название товара — подпись в окне печати этикеток. */
  productName: string;
  /** Шаблон этикетки действующего вида — вариант «Как у вида» в окне фасовки. */
  kindLabelTemplate: LabelTemplate | null;
  category: CatalogCategory | null;
}

export function ProductInventorySection({ productId, productSlug, productName, kindLabelTemplate, category }: Props) {
  const skus = useProductSkus(productId);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CatalogSku | null>(null);
  const [printing, setPrinting] = useState<CatalogSku | null>(null);
  // Оба хука зовём всегда: `a() || b()` пропускал бы второй — нарушение правил хуков.
  const canViewWms = useHasPermission("SHOP_WMS_VIEW");
  const canManageWms = useHasPermission("SHOP_WMS_MANAGE");
  const canPrint = canViewWms || canManageWms;

  const unitsById = new Map(skus.units.map((u) => [u.id, u]));
  const markingRequired = category?.markingEnabled ?? false;
  const codes = skus.skus.map((s) => s.code);

  return (
    <SectionCard
      icon={<Package />}
      title="Фасовки"
      action={
        <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          Фасовка
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        {markingRequired ? (
          <NoticeStrip tone="warning">Категория подлежит маркировке «Честный знак» — у фасовок нужны GTIN и ТН ВЭД</NoticeStrip>
        ) : null}
        {skus.loading && skus.skus.length === 0 ? (
          <p className="type-caption text-on-surface-variant">Загружаем фасовки…</p>
        ) : skus.skus.length === 0 ? (
          <p className="type-body text-on-surface-variant">Фасовок нет. Добавьте хотя бы одну — без неё товар нельзя продать.</p>
        ) : (
          <ul aria-label="Фасовки товара" className="divide-y divide-[var(--border)]">
            {skus.skus.map((s) => (
              <ProductSkuRow
                key={s.id}
                sku={s}
                unit={unitsById.get(s.saleUnitId)}
                markingRequired={markingRequired}
                onEdit={() => setEditing(s)}
                onDelete={() => void skus.remove(s)}
                onRestore={() => void skus.restore(s)}
                onPrint={canPrint ? () => setPrinting(s) : undefined}
              />
            ))}
          </ul>
        )}
      </div>

      {creating || editing ? (
        <SkuEditDialog
          open
          productId={productId}
          sku={editing}
          suggestedCode={creating ? generateSkuCode(productSlug, codes) : undefined}
          onGenerateCode={() => generateSkuCode(productSlug, codes)}
          units={skus.units}
          markingRequired={markingRequired}
          tnvedHint={category?.tnvedHint ?? null}
          kindLabelTemplate={kindLabelTemplate}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await skus.refresh();
          }}
        />
      ) : null}

      <SkuLabelPrintDialog
        sku={printing}
        productName={productName}
        unit={printing ? unitsById.get(printing.saleUnitId) : undefined}
        onClose={() => setPrinting(null)}
      />
      <ConfirmDialog request={skus.confirmRequest} onSettle={skus.settleConfirm} />
    </SectionCard>
  );
}
